import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { isUndefined } from 'lodash'

import { appUpdate } from '@e3dc-react/shell/State/App/AppActions'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Paper } from '@e3dc-react/shell/Elements/Paper'
import { refreshApiView } from '@e3dc-react/shell/State/ApiView/ApiViewActions'
import { useUser } from 'src/Hooks/useUser'
import { useCompanyInstallersHelper } from 'src/Libs/company-settings'
import { CompanyModel } from '../models/company.model'
import { useCompanyDataTabs } from '../hooks/use-company-tabs'
import CompanyUsersTable from './CompanyUsersPermissionsTable'
import { useCompanies } from '../hooks/use-companies'
import { usePageActionsContext } from 'src/Libs/header/context'

/**
 * The component does provide the actual content for the company users.
 *
 * - the table content is a composition of multiple data source company data, company users data and error notifications
 * - company data is used for the header component, to identify who is the "Manager" and does the company has access to shop (needed for the tooltip reasons)
 * - company users data is composed of the actual user and additional provided information about available error notifications
 * - updating the error notification settings and shop permissioms are an instant update, and the reference id changes with every update
 */

const CompanyUserPermissions: React.FC = () => {
  const { t } = useCustomTranslation()
  const { setActiveTab } = useCompanyDataTabs()
  const navigate = useNavigate()
  const { dispatchPageActions } = usePageActionsContext()
  const { userData } = useUser()
  const { id } = useParams<{ id: string }>()
  const companyId = Number(id)
  const { getCompany } = useCompanies()
  const [company, setCompany] = useState<CompanyModel>()
  const dispatch = useDispatch()
  const { tableItems, installersLoading, invitesLoading } = useCompanyInstallersHelper(companyId, userData)

  useEffect(() => {
    setActiveTab(1)
  }, [setActiveTab])

  useEffect(() => {
    dispatchPageActions?.([{ position: 'left', type: 'back', onClick: () => navigate('/overview/companies') }])

    return () => {
      dispatchPageActions?.([])
    }
  }, [dispatchPageActions, navigate])

  useEffect(() => {
    dispatch(appUpdate({ title: company?.name }))
  }, [company]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isUndefined(companyId)) return
    getCompany(companyId)
      .then(company => {
        setCompany(company)
      })
      .catch(error => console.error(error))
  }, [companyId]) // eslint-disable-line react-hooks/exhaustive-deps

  const refreshTable = (): void => {
    getCompany(companyId)
      .then(company => {
        setCompany(company)
        dispatch(refreshApiView())
      })
      .catch(error => console.error(error))
  }

  return (
    <Paper title={t('user.company.accessRights')}>
      {company && (
        <CompanyUsersTable
          installers={tableItems}
          loading={installersLoading || invitesLoading}
          companyId={companyId}
          companyManager={company?.manager}
          companyHasShop={company.shopPermission}
          companyName={company.name}
          refreshTable={refreshTable}
        />
      )}
    </Paper>
  )
}

export default CompanyUserPermissions
